import { DateTime } from 'luxon';

import { categoriesSelectors } from '../categories';
import { chartOfAccountsTypes } from '../chart-of-accounts';
import type { ChartOfAccount, ChartOfAccountProps } from '../chart-of-accounts/chartOfAccountsTypes';
import { transactionsUtils } from '../transactions';
import type { TransactionProps } from '../transactions/transactionsTypes';

import type { RecurrenceType } from './customFrequencyTypes';
import { FrequencyEnum } from './frequency';
import type { STType, ScheduledTransaction } from './scheduledTransactionsTypes';
import * as scheduledTransactionsTypes from './scheduledTransactionsTypes';

export const frequencyAliasTypes = {
  everyWeekSimplifi: 'Every week',
  everyTwoWeeksSimplifi: 'Every two weeks',
  twiceAMonthSimplifi: 'Twice a month',
  everyMonthSimplifi: 'Every month',
  every2MonthsSimplifi: 'Every 2 months',
  everyQuarterSimplifi: 'Every quarter',
  every6MonthsSimplifi: 'Every 6 months',
  everyYearSimplifi: 'Every year',
  custom: 'Custom',
} as const;

export type FrequencyAlias = (typeof frequencyAliasTypes)[keyof typeof frequencyAliasTypes];

export const frequencyAliases = [
  frequencyAliasTypes.everyWeekSimplifi,
  frequencyAliasTypes.everyTwoWeeksSimplifi,
  frequencyAliasTypes.twiceAMonthSimplifi,
  frequencyAliasTypes.everyMonthSimplifi,
  frequencyAliasTypes.every2MonthsSimplifi,
  frequencyAliasTypes.everyQuarterSimplifi,
  frequencyAliasTypes.every6MonthsSimplifi,
  frequencyAliasTypes.everyYearSimplifi,
];

export const weekDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

export const recurrenceFromFrequencyAlias = (frequencyAlias: FrequencyAlias, dueOn?: string) => {
  let recurrence: RecurrenceType | undefined;

  if (dueOn && frequencyAlias) {
    const date = DateTime.fromISO(dueOn);

    switch (frequencyAlias) {
      case frequencyAliasTypes.everyWeekSimplifi:
        {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: FrequencyEnum.WEEKLY,
            interval: 1,
            ...(day && {
              byDay: [day],
            }),
          };
        }
        break;
      case frequencyAliasTypes.everyTwoWeeksSimplifi:
        {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: FrequencyEnum.WEEKLY,
            interval: 2,
            ...(day && {
              byDay: [day],
            }),
          };
        }
        break;
      case frequencyAliasTypes.twiceAMonthSimplifi:
        {
          const daysInMonth = 31;
          let day1 = date?.day;
          day1 = day1 > 28 ? day1 - daysInMonth - 1 : day1;
          const day2raw = date?.day + 15;
          let day2 = day2raw % daysInMonth;
          day2 = day2 && day2 <= 28 ? day2 : day2raw - daysInMonth - 1;
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 1,
            ...(day1 && day2
              ? {
                  byMonthDay: [day1, day2],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyMonthSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 1,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.every2MonthsSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 2,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyQuarterSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 3,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.every6MonthsSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 6,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyYearSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 12,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      default:
    }
  }

  return recurrence;
};

export const frequencyAliasFromRecurrence = (recurrence: any) => {
  let frequencyAlias: FrequencyAlias = frequencyAliasTypes.custom;

  if (recurrence && recurrence.frequency && recurrence.interval) {
    if (recurrence.frequency === FrequencyEnum.WEEKLY && recurrence.interval === 1) {
      frequencyAlias = frequencyAliasTypes.everyWeekSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.WEEKLY && recurrence.interval === 2) {
      frequencyAlias = frequencyAliasTypes.everyTwoWeeksSimplifi;
    } else if (
      recurrence.frequency === FrequencyEnum.MONTHLY &&
      recurrence.interval === 1 &&
      recurrence.byMonthDay &&
      recurrence.byMonthDay.length === 2
    ) {
      frequencyAlias = frequencyAliasTypes.twiceAMonthSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 1) {
      frequencyAlias = frequencyAliasTypes.everyMonthSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 6) {
      frequencyAlias = frequencyAliasTypes.every6MonthsSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 2) {
      frequencyAlias = frequencyAliasTypes.every2MonthsSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 3) {
      frequencyAlias = frequencyAliasTypes.everyQuarterSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 12) {
      frequencyAlias = frequencyAliasTypes.everyYearSimplifi;
    } else {
      frequencyAlias = frequencyAliasTypes.custom;
    }
  }

  return frequencyAlias;
};

export const stTypes = [
  scheduledTransactionsTypes.STTypeEnum.BILL,
  scheduledTransactionsTypes.STTypeEnum.SUBSCRIPTION,
  scheduledTransactionsTypes.STTypeEnum.INCOME,
  scheduledTransactionsTypes.STTypeEnum.TRANSFER,
];

export const STTypeNamesEnum = Object.freeze({
  [scheduledTransactionsTypes.STTypeEnum.EXPENSE]: 'Bill', // Expense
  [scheduledTransactionsTypes.STTypeEnum.INCOME]: 'Income',
  [scheduledTransactionsTypes.STTypeEnum.TRANSFER]: 'Transfer',
  [scheduledTransactionsTypes.STTypeEnum.BILL]: 'Bill',
  [scheduledTransactionsTypes.STTypeEnum.SUBSCRIPTION]: 'Subscription',
  [scheduledTransactionsTypes.STTypeEnum.UNKNOWN]: 'Unknown',
});

export const typeFromScheduledTransaction = (scheduledTransaction: ScheduledTransaction): STType => {
  let stType: STType = scheduledTransactionsTypes.STTypeEnum.UNKNOWN;

  if (scheduledTransaction.transaction?.isBill) {
    stType = scheduledTransactionsTypes.STTypeEnum.BILL;
  } else if (scheduledTransaction.transaction?.isSubscription) {
    stType = scheduledTransactionsTypes.STTypeEnum.SUBSCRIPTION;
  } else if (scheduledTransaction.transaction?.coa) {
    const { coa } = scheduledTransaction.transaction;
    stType = typeFromCOA(coa);
  }
  return stType;
};

export const typeFromCOA = (coa: ChartOfAccountProps): STType => {
  let stType: STType = scheduledTransactionsTypes.STTypeEnum.UNKNOWN;

  if (coa) {
    if (coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isIncomeCat(null, coa.id)) {
      stType = scheduledTransactionsTypes.STTypeEnum.INCOME;
    } else if (
      coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ||
      coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT
    ) {
      stType = scheduledTransactionsTypes.STTypeEnum.TRANSFER;
    } else {
      stType = scheduledTransactionsTypes.STTypeEnum.BILL;
    }
  }
  return stType;
};

export const negatorFromCOA = (coa: ChartOfAccount) => {
  let negator = -1;

  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isIncomeCat(null, coa.id)) {
    negator = +1;
  } else {
    negator = -1;
  }

  return negator;
};

export const negatorFromType = (type: STType) => {
  let negator = -1;

  switch (type) {
    case scheduledTransactionsTypes.STTypeEnum.BILL:
    case scheduledTransactionsTypes.STTypeEnum.SUBSCRIPTION:
    case scheduledTransactionsTypes.STTypeEnum.TRANSFER:
    case scheduledTransactionsTypes.STTypeEnum.EXPENSE:
    case scheduledTransactionsTypes.STTypeEnum.UNKNOWN:
      negator = -1;
      break;

    case scheduledTransactionsTypes.STTypeEnum.INCOME:
      negator = +1;
      break;

    default:
      negator = -1;
  }

  return negator;
};

export const getSchedTxnType = (txn?: TransactionProps | null) => {
  if (txn?.isBill) {
    return 'bill';
  }
  if (txn?.isSubscription) {
    return 'subscription';
  }
  if (transactionsUtils.isSplitTxn(txn)) {
    return 'item';
  }
  if (txn?.coa?.type === 'ACCOUNT') {
    return 'transfer';
  }
  if (txn?.coa?.type === 'CATEGORY') {
    return categoriesSelectors.isIncomeCat(null, txn.coa.id) ? 'income' : 'bill';
  }
  return 'item';
};

export const getDisplayName = (txn: TransactionProps) => {
  const investmentAction =
    txn && txn.investmentTxnType && transactionsUtils?.InvestmentTxnActionsByType[txn.investmentTxnType];
  const investmentActionFormatted = investmentAction ? `${investmentAction} ` : '';
  const displayName = txn?.type === 'INVESTMENT' ? `${investmentActionFormatted}${txn?.symbol}` : txn?.payee;

  return displayName;
};
